import React, { useEffect, useState } from 'react';
import { LoadingPage } from '../../../../../shared/LoadingPage';
import ErrorPage from '../../../../../shared/ErrorPage';
import DetailBar from './components/DetailBar';
import EditorialSummaryChart from '../../components/EditorialSummaryChart';
import ComparisonControls from '../../components/ComparisonControls';
import moment from 'moment';
import { Col, Row } from 'antd';
import EditorialDetailsSignUpsLoader from '../../../../../../core/components/editorial/editorialdetails/signUps/EditorialDetailsSignUpsLoader';
import EditorialDetailsSignUpsController from '../../../../../../core/components/editorial/editorialdetails/signUps/EditorialDetailsSignUpsController';
import SignUpsTable from './components/SignUpsTable';
import {
  COMPARISON_MODES,
  getComparisonRange,
  buildComparisonSeries,
  computeDeltas,
} from '../../utils/comparison';

const SU_DELTA_KEYS = ['signUps', 'clicks', 'customers'];
const SU_CHART_KEYS = ['clicks', 'signUps', 'customers'];

const day = (d) => moment(d).format('YYYY-MM-DD');
const filterByRange = (rows, field, [start, end]) => {
  const startStr = day(start);
  const endStr = day(end);
  return (rows || []).filter((r) => {
    const t = moment(r[field], 'YYYY-MM-DD');
    return t.isSameOrAfter(startStr) && t.isSameOrBefore(endStr);
  });
};

const EditorialSignUpsDetailsView = (props) => {
  return (
    <EditorialDetailsSignUpsLoader {...props}>
      <EditorialDetailsSignUpsController>
        <SignUpsTab />
      </EditorialDetailsSignUpsController>
    </EditorialDetailsSignUpsLoader>
  );
};

const SignUpsTab = ({
  reportDataSummary,
  reportSummaryLoading,
  reportSummaryError,
  startDate,
  endDate,
  signUpsListNormalized,
  reportListLoading,
  reportListError,
  filterByDate,
}) => {
  const [summaryData, setSummaryData] = useState();
  const [signUpList, setSignUpList] = useState();
  const [selectedStartEndDate, setSelectedStartEndDate] = useState();
  const [currentRange, setCurrentRange] = useState([moment(startDate), moment(endDate)]);
  const [compareMode, setCompareMode] = useState(COMPARISON_MODES.OFF);
  const [customRange, setCustomRange] = useState(null);
  const [deltas, setDeltas] = useState(null);
  const [comparisonByDate, setComparisonByDate] = useState([]);

  useEffect(() => {
    if (reportDataSummary) setSummaryData(reportDataSummary.summary);
    if (signUpsListNormalized) {
      //If the summary data is loading this means that the date picker was used before the list was loaded.
      //Adjust the data accordingly by using the list values
      if (summaryData && summaryData.loading) {
        const result = filterByDate(selectedStartEndDate[0], selectedStartEndDate[1]);
        if (result) {
          setSummaryData(result.summary);
          setSignUpList(result.signUpList);
        }
      } else {
        setSignUpList(signUpsListNormalized);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportDataSummary, signUpsListNormalized]);

  const byDates = reportDataSummary && reportDataSummary.byDates;

  useEffect(() => {
    if (!byDates || !compareMode || compareMode === COMPARISON_MODES.OFF) {
      setDeltas(null);
      setComparisonByDate([]);
      return;
    }
    const comparisonRange = getComparisonRange(compareMode, currentRange, customRange);
    if (!comparisonRange) {
      setDeltas(null);
      setComparisonByDate([]);
      return;
    }
    const cur = filterByDate(currentRange[0], currentRange[1]);
    const cmp = filterByDate(comparisonRange[0], comparisonRange[1]);
    if (cur && cmp) {
      setDeltas(computeDeltas(cur.summary, cmp.summary, SU_DELTA_KEYS, comparisonRange));
    }
    setComparisonByDate(
      buildComparisonSeries(
        filterByRange(byDates, 'date', currentRange),
        filterByRange(byDates, 'date', comparisonRange),
        currentRange,
        comparisonRange,
        SU_CHART_KEYS,
        'date'
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareMode, customRange, currentRange, byDates]);

  if (reportSummaryLoading) return <LoadingPage />;
  if (reportSummaryError || reportListError) return <ErrorPage />;

  const dropDownOptions = [
    { key: 'clicks', display: 'Impressions' },
    { key: 'signUps', display: 'Sign Ups' },
    { key: 'customers', display: 'Customers' },
  ];

  const comparisonLabel = deltas && deltas.signUps ? deltas.signUps.ranges : '';

  return (
    <React.Fragment>
      <DetailBar startDate={startDate} endDate={endDate} {...summaryData} deltas={deltas} />
      <ComparisonControls
        mode={compareMode}
        onModeChange={(mode) => setCompareMode(mode)}
        customRange={customRange}
        onCustomRangeChange={(dates) => setCustomRange(dates)}
        comparisonLabel={comparisonLabel}
      />
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <EditorialSummaryChart
            id={'signUps'}
            byDate={byDates}
            xAxis="date"
            options={dropDownOptions}
            defaultLeftYValue={dropDownOptions[0]}
            defaultRightYValue={dropDownOptions[1]}
            has6MonDisables={false}
            allTimeDateRange={[
              moment(startDate).format('YYYY-MM-DD'),
              moment(endDate).format('YYYY-MM-DD'),
            ]}
            comparisonByDate={comparisonByDate}
            onDatePickerChange={({ dates }) => {
              setCurrentRange(dates);
              const result = filterByDate(dates[0], dates[1]);
              if (result) {
                setSummaryData(result.summary);
                setSignUpList(result.signUpList);
                setSelectedStartEndDate(dates);
              }
            }}
          />
        </Col>
        <Col xs={24}>
          <SignUpsTable data={signUpList} loading={reportListLoading} />
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default EditorialSignUpsDetailsView;
